/**
 * ObstructionFilterFields
 *
 * Controlled obstruction filter inputs shared by the owner form (frozen base
 * filters) and the public viewer narrowing panel. Mirrors the app's full
 * obstruction filter set: types, statuses, severities, traffic impacts, road,
 * contractor, and the planned-start date range.
 */
import { memo, useMemo } from 'react';
import { Stack, TextInput } from '@mantine/core';
import {
  IconAlertTriangle,
  IconBuilding,
  IconFlag,
  IconListCheck,
  IconRoad,
  IconRoute,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { CollapsingMultiSelect } from '@/components/ui/CollapsingMultiSelect';
import { ICON_SIZE_SM } from '@/constants/ui';
import type { ShareViewerFilters } from '@/types/share';

import {
  SHARE_OBSTRUCTION_TYPE_VALUES,
  SHARE_OBSTRUCTION_STATUS_VALUES,
  SHARE_OBSTRUCTION_SEVERITY_VALUES,
  SHARE_OBSTRUCTION_IMPACT_VALUES,
} from '../shareConstants';
import { restrictedOptions } from './shareFilterOptions';
import { ShareFilterDateRange } from './ShareFilterDateRange';

interface ObstructionFilterFieldsProps {
  value: ShareViewerFilters;
  onPatch: (patch: Partial<ShareViewerFilters>) => void;
  /** Owner-shared subsets; empty / missing shows all values for that dimension. */
  allowedTypes?: string[];
  allowedStatuses?: string[];
  allowedSeverities?: string[];
  allowedImpacts?: string[];
}

export const ObstructionFilterFields = memo(function ObstructionFilterFields({
  value,
  onPatch,
  allowedTypes,
  allowedStatuses,
  allowedSeverities,
  allowedImpacts,
}: ObstructionFilterFieldsProps) {
  const { t } = useTranslation();

  const typeOptions = useMemo(
    () =>
      restrictedOptions(
        SHARE_OBSTRUCTION_TYPE_VALUES,
        allowedTypes,
        'obstructions.type',
        t
      ),
    [t, allowedTypes]
  );
  const statusOptions = useMemo(
    () =>
      restrictedOptions(
        SHARE_OBSTRUCTION_STATUS_VALUES,
        allowedStatuses,
        'obstructions.status',
        t
      ),
    [t, allowedStatuses]
  );
  const severityOptions = useMemo(
    () =>
      restrictedOptions(
        SHARE_OBSTRUCTION_SEVERITY_VALUES,
        allowedSeverities,
        'obstructions.severity',
        t
      ),
    [t, allowedSeverities]
  );
  const impactOptions = useMemo(
    () =>
      restrictedOptions(
        SHARE_OBSTRUCTION_IMPACT_VALUES,
        allowedImpacts,
        'obstructions.impact',
        t
      ),
    [t, allowedImpacts]
  );

  return (
    <Stack gap="xs">
      <CollapsingMultiSelect
        label={t('share.filters.type')}
        placeholder={t('obstructions.filters.typePlaceholder')}
        leftSection={<IconAlertTriangle size={ICON_SIZE_SM} />}
        data={typeOptions}
        value={value.obstructionTypes ?? []}
        onChange={(next) => onPatch({ obstructionTypes: next })}
        clearable
      />
      <CollapsingMultiSelect
        label={t('share.filters.status')}
        placeholder={t('obstructions.filters.statusPlaceholder')}
        leftSection={<IconListCheck size={ICON_SIZE_SM} />}
        data={statusOptions}
        value={value.obstructionStatuses ?? []}
        onChange={(next) => onPatch({ obstructionStatuses: next })}
        clearable
      />
      <CollapsingMultiSelect
        label={t('share.filters.severity')}
        placeholder={t('obstructions.filters.severityPlaceholder')}
        leftSection={<IconFlag size={ICON_SIZE_SM} />}
        data={severityOptions}
        value={value.obstructionSeverities ?? []}
        onChange={(next) => onPatch({ obstructionSeverities: next })}
        clearable
      />
      <CollapsingMultiSelect
        label={t('share.filters.impact')}
        placeholder={t('obstructions.filters.impactPlaceholder')}
        leftSection={<IconRoute size={ICON_SIZE_SM} />}
        data={impactOptions}
        value={value.obstructionImpacts ?? []}
        onChange={(next) => onPatch({ obstructionImpacts: next })}
        clearable
      />
      <TextInput
        label={t('share.filters.location')}
        placeholder={t('obstructions.filters.locationPlaceholder')}
        leftSection={<IconRoad size={ICON_SIZE_SM} />}
        value={value.obstructionRoad ?? ''}
        onChange={(event) =>
          onPatch({ obstructionRoad: event.currentTarget.value })
        }
      />
      <TextInput
        label={t('share.filters.contractor')}
        placeholder={t('obstructions.filters.contractorPlaceholder')}
        leftSection={<IconBuilding size={ICON_SIZE_SM} />}
        value={value.obstructionContractor ?? ''}
        onChange={(event) =>
          onPatch({ obstructionContractor: event.currentTarget.value })
        }
      />
      <ShareFilterDateRange
        fromLabel={t('share.filters.dateFrom')}
        toLabel={t('share.filters.dateTo')}
        fromValue={value.obstructionDateFrom}
        toValue={value.obstructionDateTo}
        onFromChange={(iso) => onPatch({ obstructionDateFrom: iso })}
        onToChange={(iso) => onPatch({ obstructionDateTo: iso })}
      />
    </Stack>
  );
});
